// ✅ src/pages/VehicleV2/AddTyreSLT.jsx  (FULL FILE)
// Backend mount:
//   app.use("/api/addTyreSLT", addTyreSLT);
//
// API used:
//   GET  https://nissi-india.com/api/addTyreSLT/init?loginUserId=&branchId=
//   GET  https://nissi-india.com/api/addTyreSLT/dropdowns?branchId=&type=N|R
//   GET  https://nissi-india.com/api/addTyreSLT/list?branchId=&type=&tyreSerialNo=&tyreSize=&position=&basis=&tyreId=
//   POST https://nissi-india.com/api/addTyreSLT/update-row
//   POST https://nissi-india.com/api/addTyreSLT/retread
//   POST https://nissi-india.com/api/addTyreSLT/transfer
//   POST https://nissi-india.com/api/addTyreSLT/scrap
//   POST https://nissi-india.com/api/addTyreSLT/delete-row
//   POST https://nissi-india.com/api/addTyreSLT/submit
//
// ✅ Updated to match attached AddTyreListing style:
// - Top header redesigned
// - Filter section styled like ASPX/listing screen
// - Show/Hide filters button added
// - Search + View All buttons centered
// - Table scroll only inside table content
// - Sticky table header while scrolling
// - Count badge added

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaArrowLeft,
  FaSave,
  FaFilter,
  FaSearch,
  FaEdit,
  FaTrash,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/addTyreSLT";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const AddTyreSLT = () => {
  const navigate = useNavigate();
  const query = useQuery();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const loginUserId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  const branchId = Number(localStorage.getItem("branchid") || 8);

  // from listing: /vehicle/AddTyreSLT?tyreId=xx&type=N|R
  const tyreId = toInt(query.get("tyreId"), 0);
  const initialType = (query.get("type") || "N").toUpperCase();

  /* ------------------- top filter/form state ------------------- */
  const [tyreType, setTyreType] = useState(initialType); // N / R
  const [serialNo, setSerialNo] = useState("");
  const [tyreSize, setTyreSize] = useState("");
  const [position, setPosition] = useState("Rear");

  const [serialOptions, setSerialOptions] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);

  const [vehicleConnectInventory, setVehicleConnectInventory] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  // vehicle base radio
  const [vehicleBase, setVehicleBase] = useState("DAY"); // HOUR / KM / DAY
  const basisParam = useMemo(() => {
    if (vehicleBase === "HOUR") return "Hour";
    if (vehicleBase === "KM") return "KM";
    return "Day";
  }, [vehicleBase]);

  /* ------------------- grid state ------------------- */
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editRowId, setEditRowId] = useState(null);

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const smallInputClass = `w-full px-2 py-2 border rounded-lg focus:outline-none ${
    isDark
      ? "bg-gray-700/40 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  /* ------------------- API calls ------------------- */

  // 1) INIT
  const fetchInit = async () => {
    try {
      const res = await axios.get(`${API_BASE}/init`, {
        params: {
          loginUserId: toInt(loginUserId),
          branchId,
        },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load init data");
        return;
      }

      setVehicleConnectInventory(!!res.data.vehicleConnectInventory);

      if (res.data.defaultType) {
        setTyreType(String(res.data.defaultType).toUpperCase());
      }

      if (res.data.defaultBasis) {
        const b = String(res.data.defaultBasis).toLowerCase();
        if (b.includes("hour")) setVehicleBase("HOUR");
        else if (b.includes("km")) setVehicleBase("KM");
        else setVehicleBase("DAY");
      }
    } catch (e) {
      toast.error("Server error while loading init");
    }
  };

  // 2) DROPDOWNS
  const fetchDropdowns = async () => {
    try {
      const res = await axios.get(`${API_BASE}/dropdowns`, {
        params: { branchId, type: tyreType },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load dropdowns");
        setSerialOptions([]);
        setSizeOptions([]);
        return;
      }

      const s = res.data.serials || [];
      const z = res.data.sizes || [];

      setSerialOptions(
        s.map((x) => ({
          value: x.tyreSerialNo ?? x.value ?? "",
          label: x.tyreSerialNo ?? x.label ?? "",
        }))
      );

      setSizeOptions(
        z.map((x) => ({
          value: x.tyreSize ?? x.value ?? "",
          label: x.tyreSize ?? x.label ?? "",
        }))
      );
    } catch (e) {
      toast.error("Server error while loading dropdowns");
      setSerialOptions([]);
      setSizeOptions([]);
    }
  };

  // 3) GRID LIST
  const fetchGrid = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/list`, {
        params: {
          branchId,
          type: tyreType,
          tyreSerialNo: serialNo,
          tyreSize,
          position,
          basis: basisParam,
          tyreId,
        },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load grid");
        setRows([]);
        return;
      }

      const list = res.data.list || [];

      setRows(
        list.map((r) => {
          const local = r._local || {
            vehicleCode: r.vehicleCode ?? "",
            vehicleRegNo: r.vehicleRegNo ?? "",
            fittingDate: r.fittingDate ?? "",
            fittingHourKm: r.fittingHourKm ?? "",
            removingDate: r.removingDate ?? "",
            removingHourKm: r.removingHourKm ?? "",
            runningHourKm: r.runningHourKm ?? "",
            rate: r.rate ?? "",
            tyreCostPerKm: r.tyreCostPerKm ?? "",
          };

          return {
            ...r,
            tyreSerialNoText: Array.isArray(r.tyreSerialNo)
              ? r.tyreSerialNo[0]
              : r.tyreSerialNo ?? "",
            _local: local,
          };
        })
      );
    } catch (e) {
      toast.error("Server error while loading grid");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDropdowns();
    setSerialNo("");
    setTyreSize("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tyreType, branchId]);

  useEffect(() => {
    fetchGrid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tyreType, serialNo, tyreSize, position, basisParam, tyreId]);

  /* ------------------- row helpers ------------------- */

  const setRowLocal = (rowId, key, value) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? { ...r, _local: { ...(r._local || {}), [key]: value } }
          : r
      )
    );
  };

  const startEdit = (rowId) => setEditRowId(rowId);

  const cancelEdit = () => {
    setEditRowId(null);
    fetchGrid();
  };

  /* ------------------- actions ------------------- */

  // 4) UPDATE ROW
  const saveRow = async (row) => {
    try {
      const L = row._local || {};

      const payload = {
        loginUserId: toInt(loginUserId),
        branchId,
        id: toInt(row.id),
        tyreId: toInt(row.tyreId || 0),
        type: tyreType,
        vehicleBase: basisParam,
        vehicleCode: L.vehicleCode,
        vehicleRegNo: L.vehicleRegNo,
        fittingDate: L.fittingDate,
        fittingHourKm: L.fittingHourKm,
        removingDate: L.removingDate,
        removingHourKm: L.removingHourKm,
        runningHourKm: L.runningHourKm,
        rate: L.rate,
        tyreCostPerKm: L.tyreCostPerKm,
      };

      const res = await axios.post(`${API_BASE}/update-row`, payload);

      if (!res.data?.success) {
        toast.error(res.data?.message || "Update failed");
        return;
      }

      toast.success(res.data?.message || "Updated successfully");
      setEditRowId(null);
      fetchGrid();
    } catch (e) {
      toast.error("Server error while updating");
    }
  };

  // 5) RETREAD
  const doRetread = async (row) => {
    if (!window.confirm("Do you really want to Retread?")) return;
    try {
      const res = await axios.post(`${API_BASE}/retread`, {
        loginUserId: toInt(loginUserId),
        branchId,
        id: toInt(row.id),
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Retread failed");
        return;
      }

      toast.success(res.data?.message || "Retreaded");
      fetchGrid();
    } catch {
      toast.error("Server error while retread");
    }
  };

  // 6) TRANSFER
  const doTransfer = async (row) => {
    if (!window.confirm("Do you really want to Transfer?")) return;
    try {
      const res = await axios.post(`${API_BASE}/transfer`, {
        loginUserId: toInt(loginUserId),
        branchId,
        id: toInt(row.id),
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Transfer failed");
        return;
      }

      toast.success(res.data?.message || "Transferred");
      fetchGrid();
    } catch {
      toast.error("Server error while transfer");
    }
  };

  // 7) SCRAP
  const doScrap = async (row) => {
    if (!window.confirm("Do you really want to Scrap?")) return;
    try {
      const res = await axios.post(`${API_BASE}/scrap`, {
        loginUserId: toInt(loginUserId),
        branchId,
        id: toInt(row.id),
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Scrap failed");
        return;
      }

      toast.success(res.data?.message || "Scrapped");
      fetchGrid();
    } catch {
      toast.error("Server error while scrap");
    }
  };

  // 8) DELETE ROW
  const doDelete = async (row) => {
    if (!window.confirm("Do you really want to Delete?")) return;
    try {
      const res = await axios.post(`${API_BASE}/delete-row`, {
        loginUserId: toInt(loginUserId),
        branchId,
        id: toInt(row.id),
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Delete failed");
        return;
      }

      toast.success(res.data?.message || "Deleted");
      fetchGrid();
    } catch {
      toast.error("Server error while delete");
    }
  };

  // 9) SUBMIT ALL
  const submitAll = async () => {
    try {
      const payload = {
        branchId,
        loginUserId: toInt(loginUserId),
        type: tyreType,
        basis: basisParam,
        rows: rows.map((r) => ({
          id: toInt(r.id),
          tyreId: toInt(r.tyreId || 0),
          useOfTyre: r.useOfTyre || "",
          vehicleCode: r._local?.vehicleCode ?? "",
          vehicleRegNo: r._local?.vehicleRegNo ?? "",
          fittingDate: r._local?.fittingDate ?? "",
          fittingHourKm: r._local?.fittingHourKm ?? "",
          removingDate: r._local?.removingDate ?? "",
          removingHourKm: r._local?.removingHourKm ?? "",
          runningHourKm: r._local?.runningHourKm ?? "",
          rate: r._local?.rate ?? "",
          tyreCostPerKm: r._local?.tyreCostPerKm ?? "",
        })),
      };

      const res = await axios.post(`${API_BASE}/submit`, payload);

      if (!res.data?.success) {
        toast.error(res.data?.message || "Submit failed");
        return;
      }

      toast.success(res.data?.message || "Submitted successfully");
      fetchGrid();
    } catch (e) {
      toast.error("Server error while submit");
    }
  };

  /* ------------------- filter handlers ------------------- */

  const handleSearch = () => {
    fetchGrid();
  };

  const handleViewAll = () => {
    setSerialNo("");
    setTyreSize("");
    setPosition("Rear");
    setTimeout(() => {
      fetchGrid();
    }, 0);
  };

  return (
    <VehicleLayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">ADD TYRE</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        {/* Top Header row */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
          <div className="flex items-center gap-4">
            <h1
              className={`text-2xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Add Tyre
            </h1>

            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark
                  ? "bg-blue-900 text-blue-100"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {rows.length} Records
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800" +
                    (showFilters ? " bg-gray-800" : "")
                  : "border-gray-300 text-gray-700 hover:bg-gray-50" +
                    (showFilters ? " bg-gray-50" : "")
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        </div>

        {/* Type Toggle */}
        <div className="flex items-center gap-6 mb-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={tyreType === "N"}
              onChange={() => setTyreType("N")}
            />
            <span className={`${isDark ? "text-gray-200" : "text-gray-800"}`}>
              New Tyre
            </span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={tyreType === "R"}
              onChange={() => setTyreType("R")}
            />
            <span className={`${isDark ? "text-gray-200" : "text-gray-800"}`}>
              Retreaded Tyre
            </span>
          </label>
        </div>

        {/* Filter Form */}
        {showFilters && (
          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="mb-6">
              <div
                className={`h-10 flex items-center px-4 font-semibold tracking-wide ${
                  isDark ? "bg-white/10 text-white" : "bg-blue-100 text-blue-900"
                }`}
              >
                ADD TYRE FILTER
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-6">
              {/* LEFT */}
              <div className="space-y-5">
                <div className="grid grid-cols-12 items-center gap-4">
                  <label
                    className={`col-span-4 text-sm ${
                      isDark ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Tyre Serial No
                  </label>
                  <div className="col-span-8">
                    <select
                      value={serialNo}
                      onChange={(e) => setSerialNo(e.target.value)}
                      className={inputClass}
                    >
                      <option value="">Select</option>
                      {serialOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-12 items-center gap-4">
                  <label
                    className={`col-span-4 text-sm ${
                      isDark ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Tyre Size / Ply Rating
                  </label>
                  <div className="col-span-8">
                    <select
                      value={tyreSize}
                      onChange={(e) => setTyreSize(e.target.value)}
                      className={inputClass}
                    >
                      <option value="">Select</option>
                      {sizeOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* RIGHT */}
              <div className="space-y-5">
                <div className="grid grid-cols-12 items-center gap-4">
                  <label
                    className={`col-span-4 text-sm ${
                      isDark ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Position
                  </label>
                  <div className="col-span-8">
                    <select
                      value={position}
                      onChange={(e) => setPosition(e.target.value)}
                      className={inputClass}
                    >
                      <option value="Front">Front</option>
                      <option value="Rear">Rear</option>
                    </select>
                  </div>
                </div>

                {vehicleConnectInventory ? (
                  <div className="grid grid-cols-12 items-center gap-4">
                    <label
                      className={`col-span-4 text-sm ${
                        isDark ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Info
                    </label>
                    <div
                      className={`col-span-8 text-sm ${
                        isDark ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      Item Name enabled (Inventory connect).
                    </div>
                  </div>
                ) : (
                  <div className="hidden md:block h-[52px]" />
                )}
              </div>
            </div>

            {/* Vehicle Base */}
            <div className="mt-8">
              <div className="flex flex-wrap items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={vehicleBase === "HOUR"}
                    onChange={() => setVehicleBase("HOUR")}
                  />
                  <span>Hour Base Vehicle</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={vehicleBase === "KM"}
                    onChange={() => setVehicleBase("KM")}
                  />
                  <span>Km Base Vehicle</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={vehicleBase === "DAY"}
                    onChange={() => setVehicleBase("DAY")}
                  />
                  <span>Day Base Vehicle</span>
                </label>
              </div>
            </div>

            {/* Buttons centered */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
              <button
                onClick={handleSearch}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-10 py-3 rounded-lg font-semibold shadow-md hover:shadow-lg transition`}
              >
                <FaSearch />
                Search
              </button>

              <button
                onClick={handleViewAll}
                className={`flex items-center gap-2 px-10 py-3 rounded-lg font-semibold border shadow-sm hover:shadow transition ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-700"
                    : "border-gray-300 text-gray-800 hover:bg-gray-100"
                }`}
              >
                View All
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div
          className={`rounded-xl shadow-sm border overflow-hidden ${
            isDark ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="overflow-x-auto">
            <div className="max-h-[420px] overflow-y-auto">
              <table className="min-w-[1800px] w-full">
                <thead className={`${buttonClass} text-white sticky top-0 z-10`}>
                  <tr>
                    <th className="px-3 py-4 text-center font-semibold">S.No</th>
                    <th className="px-3 py-4 text-center font-semibold">
                      Tyre SerialNo
                    </th>
                    <th className="px-3 py-4 text-center font-semibold">
                      Tyre Size
                    </th>
                    <th className="px-3 py-4 text-center font-semibold">
                      Tyre Position
                    </th>
                    <th className="px-3 py-4 text-center font-semibold">
                      Vehicle Code
                    </th>
                    <th className="px-3 py-4 text-center font-semibold">
                      Vehicle RegNo
                    </th>
                    <th className="px-3 py-4 text-center font-semibold">
                      Fitting Date
                    </th>
                    <th className="px-3 py-4 text-center font-semibold">
                      Fitting Hour/KM
                    </th>
                    <th className="px-3 py-4 text-center font-semibold">
                      Removing Date
                    </th>
                    <th className="px-3 py-4 text-center font-semibold">
                      Removing Hour/KM
                    </th>
                    <th className="px-3 py-4 text-center font-semibold">
                      Running Hour/KM
                    </th>
                    <th className="px-3 py-4 text-center font-semibold">Rate</th>
                    <th className="px-3 py-4 text-center font-semibold">
                      Tyre Cost Per Km
                    </th>
                    <th className="px-3 py-4 text-center font-semibold">
                      Retread / Original
                    </th>
                    <th className="px-3 py-4 text-center font-semibold">
                      Retread
                    </th>
                    <th className="px-3 py-4 text-center font-semibold">
                      Transfer
                    </th>
                    <th className="px-3 py-4 text-center font-semibold">Scrap</th>
                    <th className="px-3 py-4 text-center font-semibold">Edit</th>
                    <th className="px-3 py-4 text-center font-semibold">
                      Delete
                    </th>
                    <th className="px-3 py-4 text-center font-semibold">
                      Branch
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan={20} className="px-4 py-10 text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={20} className="px-4 py-10 text-center">
                        No records found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const editing = editRowId === r.id;

                      return (
                        <tr
                          key={r.id || idx}
                          className={`transition-colors duration-200 ${
                            isDark ? "bg-gray-900" : "bg-white"
                          }`}
                        >
                          <td
                            className={`px-3 py-3 text-center text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {r.slno ?? idx + 1}
                          </td>

                          <td
                            className={`px-3 py-3 text-center text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {r.tyreSerialNoText || ""}
                          </td>

                          <td
                            className={`px-3 py-3 text-center text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {r.tyreSize || ""}
                          </td>

                          <td
                            className={`px-3 py-3 text-center text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {r.tyrePosition || ""}
                          </td>

                          <td className="px-2 py-2">
                            <input
                              disabled={!editing}
                              value={r._local?.vehicleCode ?? ""}
                              onChange={(e) =>
                                setRowLocal(r.id, "vehicleCode", e.target.value)
                              }
                              className={smallInputClass}
                            />
                          </td>

                          <td className="px-2 py-2">
                            <input
                              disabled={!editing}
                              value={r._local?.vehicleRegNo ?? ""}
                              onChange={(e) =>
                                setRowLocal(r.id, "vehicleRegNo", e.target.value)
                              }
                              className={smallInputClass}
                            />
                          </td>

                          <td className="px-2 py-2">
                            <input
                              disabled={!editing}
                              value={r._local?.fittingDate ?? ""}
                              onChange={(e) =>
                                setRowLocal(r.id, "fittingDate", e.target.value)
                              }
                              className={smallInputClass}
                              placeholder="dd/MM/yyyy"
                            />
                          </td>

                          <td className="px-2 py-2">
                            <input
                              disabled={!editing}
                              value={r._local?.fittingHourKm ?? ""}
                              onChange={(e) =>
                                setRowLocal(r.id, "fittingHourKm", e.target.value)
                              }
                              className={smallInputClass}
                            />
                          </td>

                          <td className="px-2 py-2">
                            <input
                              disabled={!editing}
                              value={r._local?.removingDate ?? ""}
                              onChange={(e) =>
                                setRowLocal(r.id, "removingDate", e.target.value)
                              }
                              className={smallInputClass}
                              placeholder="dd/MM/yyyy"
                            />
                          </td>

                          <td className="px-2 py-2">
                            <input
                              disabled={!editing}
                              value={r._local?.removingHourKm ?? ""}
                              onChange={(e) =>
                                setRowLocal(r.id, "removingHourKm", e.target.value)
                              }
                              className={smallInputClass}
                            />
                          </td>

                          <td className="px-2 py-2">
                            <input
                              disabled={!editing}
                              value={r._local?.runningHourKm ?? ""}
                              onChange={(e) =>
                                setRowLocal(r.id, "runningHourKm", e.target.value)
                              }
                              className={smallInputClass}
                            />
                          </td>

                          <td className="px-2 py-2">
                            <input
                              disabled={!editing}
                              value={r._local?.rate ?? ""}
                              onChange={(e) =>
                                setRowLocal(r.id, "rate", e.target.value)
                              }
                              className={smallInputClass}
                            />
                          </td>

                          <td className="px-2 py-2">
                            <input
                              disabled={!editing}
                              value={r._local?.tyreCostPerKm ?? ""}
                              onChange={(e) =>
                                setRowLocal(r.id, "tyreCostPerKm", e.target.value)
                              }
                              className={smallInputClass}
                            />
                          </td>

                          <td
                            className={`px-3 py-3 text-center text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {r.status || r.useOfTyre || ""}
                          </td>

                          <td className="px-3 py-3 text-center">
                            <button
                              onClick={() => doRetread(r)}
                              className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={!!editing}
                            >
                              Retread
                            </button>
                          </td>

                          <td className="px-3 py-3 text-center">
                            <button
                              onClick={() => doTransfer(r)}
                              className="inline-flex items-center gap-1 px-3 py-2 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={!!editing}
                            >
                              Transfer
                            </button>
                          </td>

                          <td className="px-3 py-3 text-center">
                            <button
                              onClick={() => doScrap(r)}
                              className="inline-flex items-center gap-1 px-3 py-2 bg-yellow-600 text-white text-xs font-semibold rounded-lg hover:bg-yellow-700 transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={!!editing}
                            >
                              Scrap
                            </button>
                          </td>

                          <td className="px-3 py-3 text-center">
                            {editing ? (
                              <div className="flex justify-center gap-2">
                                <button
                                  onClick={() => saveRow(r)}
                                  className="inline-flex items-center gap-1 px-3 py-2 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-all duration-200 shadow-md hover:shadow-lg"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="inline-flex items-center gap-1 px-3 py-2 bg-gray-500 text-white text-xs font-semibold rounded-lg hover:bg-gray-600 transition-all duration-200 shadow-md hover:shadow-lg"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => startEdit(r.id)}
                                className="inline-flex items-center gap-1 px-3 py-2 bg-sky-600 text-white text-xs font-semibold rounded-lg hover:bg-sky-700 transition-all duration-200 shadow-md hover:shadow-lg"
                              >
                                <FaEdit />
                                Edit
                              </button>
                            )}
                          </td>

                          <td className="px-3 py-3 text-center">
                            <button
                              onClick={() => doDelete(r)}
                              className="inline-flex items-center gap-1 px-3 py-2 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={!!editing}
                            >
                              <FaTrash />
                              Delete
                            </button>
                          </td>

                          <td
                            className={`px-3 py-3 text-center text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {r.branchName || ""}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* footer submit */}
          <div className="p-4 flex justify-end border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={submitAll}
              className={`flex items-center gap-2 px-6 py-2 text-white rounded-lg shadow ${buttonClass} ${hoverClass}`}
            >
              <FaSave /> Submit
            </button>
          </div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={2500}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </VehicleLayoutWrapper>
  );
};

export default AddTyreSLT;